import { computed, reactive } from 'vue';
import { useQuery } from 'vue-query';
import { UseQueryOptions } from 'react-query/types';
import { flatten } from 'lodash';
import { bnum, forChange } from '@/lib/utils';
import QUERY_KEYS from '@/constants/queryKeys';
import { balancerSubgraphService } from '@/services/balancer/subgraph/balancer-subgraph.service';
import { balancerFarmSubgraphService } from '@/services/balancer/subgraph/balancer-farm-subgraph.service';
import { DecoratedPoolWithShares } from '@/services/balancer/subgraph/types';
import useWeb3 from '@/services/web3/useWeb3';
import useTokenLists from '../useTokenLists';
import useTokens from '../useTokens';
import useUserSettings from '../useUserSettings';
import useNetwork from '../useNetwork';
import { POOLS } from '@/constants/pools';

type UserPoolsQueryResponse = {
  pools: DecoratedPoolWithShares[];
  totalInvestedAmount: string;
  tokens: string[];
};

export default function useUserFarmPoolsQuery(
  options: UseQueryOptions<UserPoolsQueryResponse> = {}
) {
  /**
   * COMPOSABLES
   */
  const { injectTokens, prices, dynamicDataLoading } = useTokens();
  const { loadingTokenLists } = useTokenLists();
  const { account, isWalletReady } = useWeb3();
  const { currency } = useUserSettings();
  const { networkId } = useNetwork();

  /**
   * COMPUTED
   */
  const enabled = computed(
    () =>
      isWalletReady.value && account.value != null && !loadingTokenLists.value
  );

  /**
   * QUERY PROPERTIES
   */
  const queryKey = reactive(QUERY_KEYS.FarmPools.User(networkId, account));

  const queryFn = async () => {
    const farmingInfo = await balancerFarmSubgraphService.farmingInfo.get({
      id: '1'
    });

    const poolShares = await balancerFarmSubgraphService.userInfos.get({
      where: {
        address: account.value.toLowerCase()
      }
    });

    const poolSharesIds = poolShares.map(poolShare => poolShare.pid);

    const farmUserPools = await balancerFarmSubgraphService.farmPools.get({
      where: {
        pid_in: poolSharesIds.filter(i => i !== '0')
      }
    });
    const farmUserPoolsLpTokens = farmUserPools.map(
      farmUserPool => farmUserPool.lpToken
    );

    const investPools = await balancerSubgraphService.pools.get({
      where: {
        address_in: farmUserPoolsLpTokens
      }
    });

    poolShares.forEach(i => {
      const farmPool = farmUserPools.find(k => k.pid === i.pid);
      if (farmPool) {
        const investPool = investPools.find(
          j => j.address === farmPool.lpToken
        );
        if (investPool) {
          Object.assign(farmPool, investPool, { isSingleFarm: false });
        } else {
          const singleFarm = POOLS.SingleFarm[networkId.value];
          const farmInfo = singleFarm[`${farmPool.lpToken}_${farmPool.pid}`];
          Object.assign(farmPool, { isSingleFarm: true }, farmInfo);
        }
        Object.assign(farmPool, {
          userAccount: i.address,
          lpStaked: i.lpStaked
        });
      }
    });

    const tokens = flatten(farmUserPools.map(pool => [pool.lpToken]));
    await injectTokens(tokens);
    await forChange(dynamicDataLoading, false);

    const decoratedPools = await balancerFarmSubgraphService.userInfos.decorate(
      farmUserPools,
      '24h',
      prices.value,
      currency.value,
      farmingInfo
    );

    const poolsWithShares = decoratedPools.map(pool => ({
      ...pool,
      shares:
        pool.totalLpStaked && pool.lpStaked
          ? bnum(pool.totalLiquidity)
              .times(pool.lpStaked)
              .div(pool.totalLpStaked)
              .toString()
          : '0'
    }));

    const totalInvestedAmount = poolsWithShares
      .map(pool => pool.shares)
      .reduce((totalShares, shares) => totalShares.plus(shares), bnum(0))
      .toString();

    return {
      pools: poolsWithShares,
      tokens,
      totalInvestedAmount
    };
  };

  const queryOptions = reactive({
    enabled,
    ...options
  });

  return useQuery<UserPoolsQueryResponse>(queryKey, queryFn, queryOptions);
}
